'use client';

import { hashKey, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { getSdkAuthStore } from '../../auth/state/auth-state.js';
import { useSdkNetworkScope, useSdkViewerScope } from '../../provider/SdkRuntimeContext.js';
import { materializePrivateAccount, privateAccountEntityKey, privateAccountScope, stripPrivateAccountProjection, upsertPrivateAccountEntity } from '../internal/private-account-entity.js';
import { publicUserEntityKey } from '../internal/public-user-entity.js';
import { createSubscriberQueryFn } from '../internal/subscriber-query-fn.js';
import { createHook } from './createHook.js';
export function createPrivateAccountHook(spec, realtime) {
  const factory = spec.entityKeys[spec.kind];
  if (factory === undefined || spec.privateAccountEntity !== true) {
    throw new Error('Private account hook requires its entity contract');
  }
  const responseBoundary = (data, context) => writePrivateAccountResponse(context.queryClient, context.viewerScope, context.networkScope, data, context.viewerAccountId, {
    source: 'rest',
    observedAtMs: context.observedAtMs
  });
  const useBase = realtime === undefined ? createHook(spec, undefined, {
    responseBoundary
  }) : createHook(spec, realtime, {
    responseBoundary
  });
  return function usePrivateAccountHook(args, opts) {
    const result = useBase(args, opts);
    const queryClient = useQueryClient();
    const viewerScope = useSdkViewerScope();
    const networkScope = useSdkNetworkScope();
    const sourceQueryKey = factory(args).queryKey;
    const sourceQueryHash = hashKey(sourceQueryKey);
    const viewerAccountId = getSdkAuthStore().current().user?.id ?? null;
    const accountId = accountIdFrom(result.data) ?? viewerAccountId ?? undefined;
    const scope = accountId === undefined ? undefined : privateAccountScope(viewerScope, networkScope, accountId);
    const accountKey = accountId === undefined || scope === undefined ? ['accounts', 'viewer', viewerScope, 'missing-account'] : privateAccountEntityKey(scope, accountId);
    const publicKey = accountId === undefined ? ['users', 'entity', 'missing-account'] : publicUserEntityKey(accountId);
    const {
      data: account
    } = useQuery({
      queryKey: accountKey,
      queryFn: createSubscriberQueryFn(queryClient, accountKey),
      enabled: false,
      staleTime: Number.POSITIVE_INFINITY
    });
    const {
      data: publicUser
    } = useQuery({
      queryKey: publicKey,
      queryFn: createSubscriberQueryFn(queryClient, publicKey),
      enabled: false,
      staleTime: Number.POSITIVE_INFINITY
    });
    const sourceQueryKeyRef = useRef(sourceQueryKey);
    sourceQueryKeyRef.current = sourceQueryKey;
    useEffect(() => {
      void sourceQueryHash;
      const currentSourceQueryKey = sourceQueryKeyRef.current;
      const data = queryClient.getQueryData(currentSourceQueryKey);
      if (data === undefined) return;
      const projection = writePrivateAccountResponse(queryClient, viewerScope, networkScope, data, viewerAccountId, {
        source: 'hydrate',
        observedAtMs: 0
      });
      if (projection !== data) {
        // ALLOW: this factory owns the declared private route projection.
        // REASON: only the field mask remains after canonical materialization.
        const sourceKey = currentSourceQueryKey;
        queryClient.setQueryData(sourceKey, projection); // ALLOW-STATE-OWNERSHIP: field mask
      }
    }, [networkScope, queryClient, sourceQueryHash, viewerAccountId, viewerScope]);
    return new Proxy(result, {
      get(target, property, receiver) {
        if (property !== 'data') return Reflect.get(target, property, receiver);
        if (target.data === null || typeof target.data !== 'object') return target.data;
        return materializePrivateAccount(target.data, account, publicUser);
      }
    });
  };
}
function writePrivateAccountResponse(queryClient, viewerScope, networkScope, data, viewerAccountId, meta) {
  if (data === null || typeof data !== 'object') return data;
  const accountId = accountIdFrom(data) ?? viewerAccountId ?? undefined;
  if (accountId === undefined) return data;
  const raw = {
    ...data,
    id: accountId
  };
  upsertPrivateAccountEntity(queryClient, viewerScope, networkScope, raw, meta);
  return stripPrivateAccountProjection(data);
}
function accountIdFrom(data) {
  if (data === null || typeof data !== 'object') return undefined;
  const id = data.id;
  return typeof id === 'string' && id.length > 0 ? id : undefined;
}